import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, FileText, Presentation } from "lucide-react";
import { api } from "@/lib/api";
import { iconFor } from "@/lib/icons";
import { Reveal } from "@/components/Reveal";
import { FormationQR } from "@/components/FormationQR";
import { useSeo } from "@/lib/seo";

export default function Formations() {
  const [modules, setModules] = useState([]);
  useSeo("Formations financières — Mon Projet Protégé",
    "18 formations claires en format présentation : REEE, CELI, CELIAPP, REER, assurance vie, fiscalité, hypothèque, RAMQ et plus. Téléchargeables en PPTX et PDF.");

  useEffect(() => { api.modules("publie").then(setModules).catch(() => {}); }, []);

  return (
    <div data-testid="formations-page" className="max-w-7xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
      <Reveal className="max-w-2xl">
        <span className="text-xs font-bold uppercase tracking-wider text-[#C89A3E]">Formations</span>
        <h1 className="mt-2 text-4xl sm:text-5xl font-bold text-[#012141] font-heading">Modules de présentation</h1>
        <p className="mt-3 text-base text-slate-600">Chaque module est un deck de slides : une idée par écran, calculateurs inclus. Parcourez à l'écran ou téléchargez en PPTX / PDF.</p>
      </Reveal>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {modules.map((m, i) => {
          const Icon = iconFor(m.icon);
          return (
            <Reveal key={m.id} delay={(i % 3) * 60} data-testid={`formation-card-${m.slug}`}
              className="group rounded-3xl bg-white border border-[#012141]/8 overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col">
              <div className="h-24 relative flex items-center gap-3 px-6" style={{ background: `linear-gradient(120deg, ${m.color}, #012141)` }}>
                {m.cover_image ? (
                  <img src={m.cover_image} alt={m.title} className="absolute inset-0 w-full h-full object-cover opacity-90" />
                ) : (
                  <span className="grid place-items-center w-12 h-12 rounded-xl bg-white/12 text-[#C89A3E] relative"><Icon size={24} /></span>
                )}
                <span className="absolute top-3 right-4 text-[11px] font-bold px-3 py-1 rounded-full bg-white/90 text-[#012141]">{m.category}</span>
              </div>
              <div className="p-6 flex-1 flex flex-col">
                <h3 className="text-lg font-semibold text-[#012141] font-heading">{m.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{m.description}</p>
                <Link to={`/formations/${m.slug}`} data-testid={`open-module-${m.slug}`}
                  className="mt-5 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-white font-semibold hover:brightness-110 transition-all" style={{ backgroundColor: "#012141" }}>
                  Ouvrir le module <ArrowRight size={16} />
                </Link>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <FormationQR slug={m.slug} title={m.title} />
                  <a href={api.exportUrl(m.id, "pptx")} data-testid={`card-export-pptx-${m.slug}`} className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full text-xs font-semibold text-[#012141] border border-[#012141]/15 hover:bg-[#012141]/5 transition-all"><Presentation size={14} /> PPTX</a>
                  <a href={api.exportUrl(m.id, "pdf")} data-testid={`card-export-pdf-${m.slug}`} className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full text-xs font-semibold text-[#012141] border border-[#012141]/15 hover:bg-[#012141]/5 transition-all"><FileText size={14} /> PDF</a>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  );
}
